/**
 * Crisis Rescue Service
 *
 * Enhanced crisis management with seasonal awareness, intelligent task triage,
 * and application round prioritization. Coordinates with existing CrisisDetector
 * to provide comprehensive recovery strategies.
 */

import { SeasonalPatterns } from './SeasonalPatterns.js';
import { CrisisDetector } from './CrisisDetector.js';
import { addDays, differenceInDays, parseISO, format } from 'date-fns';
import { buildAutoGenOffer, canAutoGenerate } from './AutoGenFeatureMap.js';

export class CrisisRescueService {
  constructor(supabaseClient) {
    if (!supabaseClient) {
      throw new Error('CrisisRescueService requires a supabase client');
    }
    this.supabase = supabaseClient;
    this.crisisDetector = new CrisisDetector();
    this.seasonalPatterns = new SeasonalPatterns(supabaseClient);
  }

  /**
   * Analyze student crisis with enhanced seasonal awareness
   * @param {string} userId - User ID
   * @param {Date} currentDate - Current date for analysis
   * @returns {Object} Enhanced crisis analysis
   */
  async analyzeStudentCrisis(userId, currentDate = new Date()) {
    try {
      // Get all active timeline items
      const { data: timeline, error } = await this.supabase
        .from('timeline_items')
        .select('*')
        .eq('user_id', userId)
        .in('completion_status', ['not_started', 'in_progress', 'pending'])
        .order('due_date');

      if (error) throw error;

      // Use existing CrisisDetector for base analysis
      const baseAnalysis = await this.crisisDetector.assessCrisisLevel(userId, timeline || []);

      // Enhance with seasonal awareness
      const seasonalPhase = await this.seasonalPatterns.getCurrentPhase(currentDate);

      // Calculate enhanced metrics
      const now = currentDate;
      const overdue = (timeline || []).filter(task => {
        const dueDate = parseISO(task.due_date || task.scheduled_date);
        return dueDate < now && task.completion_status !== 'completed';
      });

      const urgent = (timeline || []).filter(task => {
        const dueDate = parseISO(task.due_date || task.scheduled_date);
        const daysDiff = differenceInDays(dueDate, now);
        return daysDiff >= 0 && daysDiff <= 14;
      });

      // Separate by application round
      const edTasks = (timeline || []).filter(t => t.application_round === 'early_decision');
      const eaTasks = (timeline || []).filter(t => t.application_round === 'early_action');
      const rdTasks = (timeline || []).filter(t => t.application_round === 'regular');

      // Calculate enhanced crisis level with seasonal adjustment
      const enhancedCrisisLevel = this.calculateEnhancedCrisisLevel(
        baseAnalysis.level,
        overdue.length,
        urgent.length,
        seasonalPhase
      );

      return {
        baseAnalysis,
        totalActive: (timeline || []).length,
        overdue: overdue.length,
        urgent: urgent.length,
        edTasks: edTasks.length,
        eaTasks: eaTasks.length,
        rdTasks: rdTasks.length,
        seasonalPhase,
        timeline: timeline || [],
        crisisLevel: enhancedCrisisLevel,
        contextMessage: this.seasonalPatterns.getContextualMessage(
          enhancedCrisisLevel,
          seasonalPhase,
          overdue.length,
          urgent.length
        )
      };
    } catch (error) {
      console.error('[CrisisRescueService] Analysis failed:', error);
      throw error;
    }
  }

  /**
   * Calculate enhanced crisis level with seasonal factors
   * @private
   */
  calculateEnhancedCrisisLevel(baseLevel, overdueCount, urgentCount, seasonalPhase) {
    const pressureMultiplier = this.seasonalPatterns.getPressureMultiplier(
      seasonalPhase.timeline_pressure
    );

    const crisisScore = (overdueCount * 2 + urgentCount) * pressureMultiplier;

    // Adjust thresholds based on season
    const thresholds = this.getSeasonalThresholds(seasonalPhase);

    if (crisisScore >= thresholds.emergency) return 'emergency';
    if (crisisScore >= thresholds.crisis) return 'crisis';
    if (crisisScore >= thresholds.overwhelmed) return 'overwhelmed';
    if (crisisScore >= thresholds.busy) return 'busy';

    // Consider base analysis if our calculation shows normal
    if (baseLevel === 'critical' || baseLevel === 'crisis') {
      return baseLevel === 'critical' ? 'crisis' : 'overwhelmed';
    }

    return 'manageable';
  }

  /**
   * Get seasonal crisis thresholds
   * @private
   */
  getSeasonalThresholds(seasonalPhase) {
    const baseThresholds = {
      emergency: 20,
      crisis: 12,
      overwhelmed: 6,
      busy: 3
    };

    // Adjust based on seasonal expectations
    if (seasonalPhase.crisis_threshold === 'expected_crisis') {
      // October - higher thresholds as crisis is expected
      return {
        emergency: 30,
        crisis: 20,
        overwhelmed: 10,
        busy: 5
      };
    }

    if (seasonalPhase.crisis_threshold === 'damage_control') {
      // November - very high thresholds
      return {
        emergency: 35,
        crisis: 25,
        overwhelmed: 15,
        busy: 8
      };
    }

    if (seasonalPhase.crisis_threshold === 'almost_impossible') {
      // Summer - very low thresholds
      return {
        emergency: 10,
        crisis: 6,
        overwhelmed: 3,
        busy: 1
      };
    }

    return baseThresholds;
  }

  /**
   * Generate comprehensive rescue plan with seasonal strategies
   * @param {string} userId - User ID
   * @param {Date} currentDate - Current date
   * @returns {Object} Complete rescue plan
   */
  async generateRescuePlan(userId, currentDate = new Date()) {
    try {
      const analysis = await this.analyzeStudentCrisis(userId, currentDate);
      const { timeline, seasonalPhase, crisisLevel } = analysis;

      // Apply seasonal rescue strategy
      const rescueStrategy = await this.applySeasonalRescue(
        timeline,
        seasonalPhase,
        crisisLevel,
        currentDate
      );

      // Identify auto-generation opportunities
      const autoGenOffers = this.generateAutoGenOffers(timeline);

      // Create timeline buckets
      const timelineBuckets = this.createTimelineBuckets(
        rescueStrategy.prioritizedTasks,
        seasonalPhase,
        currentDate
      );

      // Calculate expected outcomes
      const outcomes = this.calculateExpectedOutcomes(
        analysis,
        rescueStrategy,
        timelineBuckets
      );

      // Store recovery plan in history
      await this.storeRecoveryPlan(userId, analysis, rescueStrategy, outcomes);

      return {
        analysis,
        rescuePlan: timelineBuckets,
        autoGenOffers,
        contextMessage: analysis.contextMessage,
        postponedTasks: rescueStrategy.postponed,
        strategy: rescueStrategy.strategy,
        outcomes,
        actions: rescueStrategy.actions
      };
    } catch (error) {
      console.error('[CrisisRescueService] Failed to generate rescue plan:', error);
      throw error;
    }
  }

  /**
   * Apply seasonal rescue strategy
   * @private
   */
  async applySeasonalRescue(timeline, seasonalPhase, crisisLevel, currentDate) {
    const strategy = seasonalPhase.rescue_strategy;

    console.log(`[CrisisRescueService] Applying ${strategy} strategy for ${crisisLevel} crisis level`);

    switch (strategy) {
      case 'application_round_triage':
        return this.applicationRoundTriage(timeline, currentDate);

      case 'emergency_triage':
        return this.emergencyTriage(timeline, currentDate);

      case 'structured_catchup':
        return this.structuredCatchup(timeline, currentDate);

      case 'salvage_and_pivot':
        return this.salvageAndPivot(timeline, currentDate);

      case 'comprehensive_restart':
        return this.comprehensiveRestart(timeline, currentDate);

      case 'RD_optimization':
        return this.rdOptimization(timeline, currentDate);

      case 'deadline_focus':
        return this.deadlineFocus(timeline, currentDate);

      default:
        return this.standardRescue(timeline, currentDate);
    }
  }

  /**
   * Application Round Triage - Focus on ED/EA first
   * @private
   */
  applicationRoundTriage(timeline, currentDate) {
    const edTasks = timeline.filter(t => t.application_round === 'early_decision');
    const eaTasks = timeline.filter(t => t.application_round === 'early_action');
    const rdTasks = timeline.filter(t => t.application_round === 'regular');
    const generalTasks = timeline.filter(t => !t.application_round);

    // Calculate crisis priority scores
    const scoredTasks = timeline.map(task => ({
      ...task,
      crisisScore: this.calculateTaskCrisisScore(task, currentDate)
    })).sort((a, b) => b.crisisScore - a.crisisScore);

    // Identify tasks to postpone
    const postponed = rdTasks.filter(task =>
      task.can_auto_generate ||
      task.postpone_to_round === 'gap_year' ||
      task.cognitive_intensity === 'low'
    );

    const prioritized = scoredTasks.filter(task =>
      !postponed.find(p => p.id === task.id)
    );

    // Generate specific actions
    const actions = this.generateTriageActions(prioritized, postponed, currentDate);

    return {
      prioritizedTasks: prioritized,
      postponed,
      strategy: 'Focus on Early Decision/Action first, postpone Regular Decision research',
      actions
    };
  }

  /**
   * Emergency Triage - October crisis mode
   * @private
   */
  emergencyTriage(timeline, currentDate) {
    // Only absolute essentials
    const critical = timeline.filter(task =>
      task.application_round === 'early_decision' ||
      task.application_round === 'early_action' ||
      task.priority === 'critical' ||
      (task.cognitive_intensity === 'high' && task.crisis_boost_factor > 1.5)
    );

    const postponed = timeline.filter(task =>
      !critical.find(c => c.id === task.id) && (
        task.application_round === 'regular' ||
        task.can_auto_generate ||
        task.postpone_to_round !== 'never'
      )
    );

    // Generate emergency actions
    const actions = this.generateEmergencyActions(critical, postponed, currentDate);

    return {
      prioritizedTasks: critical,
      postponed,
      strategy: 'Emergency mode: ED/EA applications only, everything else waits',
      actions
    };
  }

  /**
   * Structured Catchup - August organization
   * @private
   */
  structuredCatchup(timeline, currentDate) {
    // Group by cognitive intensity and batch similar tasks
    const highCognitive = timeline.filter(t => t.cognitive_intensity === 'high');
    const mediumCognitive = timeline.filter(t => t.cognitive_intensity === 'medium');
    const lowCognitive = timeline.filter(t => t.cognitive_intensity === 'low');

    // Create batches for efficiency
    const batches = this.createTaskBatches([
      { name: 'Deep Work', tasks: highCognitive, timeSlot: 'morning' },
      { name: 'Research & Planning', tasks: mediumCognitive, timeSlot: 'afternoon' },
      { name: 'Admin & Quick Tasks', tasks: lowCognitive, timeSlot: 'evening' }
    ]);

    // Score and prioritize within batches
    const prioritized = [];
    const actions = [];

    batches.forEach(batch => {
      const scoredBatch = batch.tasks
        .map(task => ({
          ...task,
          crisisScore: this.calculateTaskCrisisScore(task, currentDate),
          batch: batch.name
        }))
        .sort((a, b) => b.crisisScore - a.crisisScore);

      prioritized.push(...scoredBatch);

      if (scoredBatch.length > 0) {
        actions.push({
          type: 'batch_creation',
          batch_name: batch.name,
          time_slot: batch.timeSlot,
          task_count: scoredBatch.length,
          estimated_hours: scoredBatch.reduce((sum, t) => sum + (t.estimated_hours || 2), 0)
        });
      }
    });

    return {
      prioritizedTasks: prioritized,
      postponed: [],
      strategy: 'Structured recovery with task batching for maximum efficiency',
      actions
    };
  }

  /**
   * Salvage and Pivot - November damage control
   * @private
   */
  salvageAndPivot(timeline, currentDate) {
    // Identify what can still be salvaged for ED/EA
    const salvageable = timeline.filter(task => {
      const dueDate = parseISO(task.due_date || task.scheduled_date);
      const daysLeft = differenceInDays(dueDate, currentDate);

      return (task.application_round === 'early_decision' || task.application_round === 'early_action') &&
             daysLeft >= 0 && daysLeft <= 7;
    });

    // Everything else pivots to RD
    const pivotToRD = timeline.filter(task =>
      !salvageable.find(s => s.id === task.id) &&
      task.application_round !== 'regular'
    );

    // Regular decision tasks get normal priority
    const rdTasks = timeline.filter(t => t.application_round === 'regular');

    const prioritized = [
      ...salvageable.map(t => ({ ...t, crisisScore: 1000 })), // Maximum priority
      ...rdTasks.map(t => ({ ...t, crisisScore: this.calculateTaskCrisisScore(t, currentDate) }))
    ].sort((a, b) => b.crisisScore - a.crisisScore);

    // Generate pivot actions
    const actions = this.generatePivotActions(salvageable, pivotToRD, rdTasks, currentDate);

    return {
      prioritizedTasks: prioritized,
      postponed: pivotToRD,
      strategy: 'Salvage remaining ED/EA tasks, pivot everything else to Regular Decision',
      actions
    };
  }

  /**
   * Comprehensive Restart - Summer reset
   * @private
   */
  comprehensiveRestart(timeline, currentDate) {
    // Complete reorganization with fresh perspective
    const allTasks = timeline.map(task => ({
      ...task,
      crisisScore: this.calculateTaskCrisisScore(task, currentDate),
      newDueDate: this.calculateOptimalDueDate(task, currentDate)
    }));

    // Redistribute across the summer
    const redistributed = this.redistributeTasks(allTasks, currentDate, 90); // 90 days of summer

    // Generate restart plan
    const actions = this.generateRestartActions(redistributed, currentDate);

    return {
      prioritizedTasks: redistributed,
      postponed: [],
      strategy: 'Complete timeline restart with optimal summer distribution',
      actions
    };
  }

  /**
   * RD Optimization - December focus
   * @private
   */
  rdOptimization(timeline, currentDate) {
    // Focus entirely on RD applications
    const rdTasks = timeline.filter(t => t.application_round === 'regular' || !t.application_round);

    // Group by college
    const byCollege = {};
    rdTasks.forEach(task => {
      const college = task.college || task.college_name || 'General';
      if (!byCollege[college]) byCollege[college] = [];
      byCollege[college].push(task);
    });

    // Prioritize colleges and create optimal sequence
    const prioritized = [];
    const actions = [];

    Object.entries(byCollege).forEach(([college, tasks]) => {
      const collegePriority = this.getCollegePriority(college, tasks);

      const scoredTasks = tasks.map(task => ({
        ...task,
        crisisScore: this.calculateTaskCrisisScore(task, currentDate) * collegePriority,
        college_group: college
      })).sort((a, b) => b.crisisScore - a.crisisScore);

      prioritized.push(...scoredTasks);

      actions.push({
        type: 'college_optimization',
        college,
        task_count: tasks.length,
        priority_multiplier: collegePriority,
        estimated_hours: tasks.reduce((sum, t) => sum + (t.estimated_hours || 2), 0)
      });
    });

    return {
      prioritizedTasks: prioritized.sort((a, b) => b.crisisScore - a.crisisScore),
      postponed: [],
      strategy: 'Optimized Regular Decision application sequence by college priority',
      actions
    };
  }

  /**
   * Deadline Focus - January final push
   * @private
   */
  deadlineFocus(timeline, currentDate) {
    // Pure deadline-driven prioritization
    const withDeadlines = timeline
      .filter(task => task.due_date || task.scheduled_date)
      .map(task => ({
        ...task,
        daysUntilDue: differenceInDays(
          parseISO(task.due_date || task.scheduled_date),
          currentDate
        )
      }))
      .sort((a, b) => a.daysUntilDue - b.daysUntilDue);

    // Group by urgency
    const immediate = withDeadlines.filter(t => t.daysUntilDue <= 3);
    const thisWeek = withDeadlines.filter(t => t.daysUntilDue > 3 && t.daysUntilDue <= 7);
    const nextWeek = withDeadlines.filter(t => t.daysUntilDue > 7 && t.daysUntilDue <= 14);
    const later = withDeadlines.filter(t => t.daysUntilDue > 14);

    const prioritized = [...immediate, ...thisWeek, ...nextWeek, ...later];

    // Generate deadline-focused actions
    const actions = this.generateDeadlineActions(immediate, thisWeek, nextWeek, currentDate);

    return {
      prioritizedTasks: prioritized,
      postponed: [],
      strategy: 'Strict deadline-based prioritization for final submission push',
      actions
    };
  }

  /**
   * Standard Rescue - Default strategy
   * @private
   */
  standardRescue(timeline, currentDate) {
    // Basic prioritization by score
    const scoredTasks = timeline.map(task => ({
      ...task,
      crisisScore: this.calculateTaskCrisisScore(task, currentDate)
    })).sort((a, b) => b.crisisScore - a.crisisScore);

    // Identify deferrables
    const deferrable = scoredTasks.filter(t =>
      t.can_auto_generate ||
      t.cognitive_intensity === 'low' ||
      t.priority === 'low'
    ).slice(Math.floor(scoredTasks.length * 0.7)); // Defer bottom 30%

    const prioritized = scoredTasks.filter(t =>
      !deferrable.find(d => d.id === t.id)
    );

    return {
      prioritizedTasks: prioritized,
      postponed: deferrable,
      strategy: 'Standard optimization based on priority and deadlines',
      actions: []
    };
  }

  /**
   * Calculate task crisis score with all factors
   * ENHANCED: Uses incompletion_cost and mvv_risk for consequence-based triage (Maximally assistive)
   * @private
   */
  calculateTaskCrisisScore(task, currentDate) {
    let score = 0;

    // ENHANCED: Incompletion Cost (0-40 points) - Catastrophic consequences take priority
    if (task.incompletion_cost !== undefined && task.incompletion_cost !== null) {
      score += task.incompletion_cost * 40; // Max +40 for catastrophic tasks
    } else {
      // Fallback to priority if no incompletion_cost
      const priorityScores = {
        'critical': 40,
        'high': 28,
        'medium': 16,
        'low': 4
      };
      score += priorityScores[task.priority] || 16;
    }

    // Application round boost (0-10 points)
    const roundScores = {
      'early_decision': 10,
      'early_action': 7,
      'regular': 3,
      'rolling': 2
    };
    score += roundScores[task.application_round] || 5;

    // Urgency based on due date (0-10 points)
    if (task.due_date || task.scheduled_date) {
      const dueDate = parseISO(task.due_date || task.scheduled_date);
      const daysToDue = differenceInDays(dueDate, currentDate);

      if (daysToDue < 0) {
        score += 10; // Overdue gets maximum urgency
      } else if (daysToDue <= 3) {
        score += 9;
      } else if (daysToDue <= 7) {
        score += 7;
      } else if (daysToDue <= 14) {
        score += 5;
      } else if (daysToDue <= 30) {
        score += 3;
      } else {
        score += 1;
      }
    }

    // Cognitive intensity factor (multiply by 1.0-1.5)
    const intensityMultipliers = {
      'high': 1.5,
      'medium': 1.2,
      'low': 1.0
    };
    score *= intensityMultipliers[task.cognitive_intensity] || 1.2;

    // Crisis boost factor
    score *= task.crisis_boost_factor || 1.0;

    // ENHANCED: MVV Risk adjustment - Low risk tasks can be rushed when needed
    if (task.mvv_risk !== undefined && task.mvv_risk !== null) {
      if (task.mvv_risk < 0.4) {
        // Low quality gap - safe to rush, reduce priority slightly
        score *= 0.85;
      }
    }

    // Penalty for auto-generable tasks
    if (task.can_auto_generate || task.can_autogenerate === 'yes') {
      score *= 0.7;
    }

    // Penalty for redistributable tasks
    if (task.is_redistributable) {
      score *= 0.9;
    }

    return Math.round(score);
  }

  /**
   * Generate auto-generation offers for incomplete tasks
   * ENHANCED: Shows ALL autogenerable tasks in crisis, with complete endpoint info
   * @private
   */
  generateAutoGenOffers(timeline) {
    // Filter to incomplete tasks that can be auto-generated
    const autogenTasks = timeline.filter(task =>
      task.completion_status !== 'completed' &&
      canAutoGenerate(task)
    );

    // Build complete offers using feature map
    const offers = autogenTasks
      .map(task => buildAutoGenOffer(task))
      .filter(offer => offer !== null);

    // Sort by time savings (most impactful first)
    return offers.sort((a, b) => (b.timeSaved || 0) - (a.timeSaved || 0));
  }

  /**
   * Create timeline buckets for visualization
   * @private
   */
  createTimelineBuckets(prioritizedTasks, seasonalPhase, currentDate) {
    const buckets = this.seasonalPatterns.getSeasonalBuckets(seasonalPhase, currentDate);

    // Distribute tasks across buckets based on urgency and dates
    const bucket1Tasks = [];
    const bucket2Tasks = [];
    const bucket3Tasks = [];

    prioritizedTasks.forEach(task => {
      if (!task.due_date && !task.scheduled_date) {
        // No date tasks go to bucket 3
        bucket3Tasks.push(task);
        return;
      }

      const dueDate = parseISO(task.due_date || task.scheduled_date);
      const daysUntilDue = differenceInDays(dueDate, currentDate);

      if (daysUntilDue <= buckets.bucket1.maxDays) {
        bucket1Tasks.push(task);
      } else if (daysUntilDue <= buckets.bucket2.maxDays) {
        bucket2Tasks.push(task);
      } else if (daysUntilDue <= buckets.bucket3.maxDays) {
        bucket3Tasks.push(task);
      } else {
        // Beyond current view, still add to bucket 3
        bucket3Tasks.push(task);
      }
    });

    return {
      bucket1: {
        ...buckets.bucket1,
        tasks: bucket1Tasks,
        totalHours: bucket1Tasks.reduce((sum, t) => sum + (t.estimated_hours || 2), 0)
      },
      bucket2: {
        ...buckets.bucket2,
        tasks: bucket2Tasks,
        totalHours: bucket2Tasks.reduce((sum, t) => sum + (t.estimated_hours || 2), 0)
      },
      bucket3: {
        ...buckets.bucket3,
        tasks: bucket3Tasks,
        totalHours: bucket3Tasks.reduce((sum, t) => sum + (t.estimated_hours || 2), 0)
      }
    };
  }

  /**
   * Calculate expected outcomes from rescue plan
   * @private
   */
  calculateExpectedOutcomes(analysis, rescueStrategy, timelineBuckets) {
    const totalTasks = analysis.timeline.length;
    const prioritizedCount = rescueStrategy.prioritizedTasks.length;
    const postponedCount = rescueStrategy.postponed.length;

    // Calculate workload reduction
    const currentDailyHours = this.calculateDailyWorkload(analysis.timeline);
    const newDailyHours = this.calculateDailyWorkload(rescueStrategy.prioritizedTasks);
    const workloadReduction = ((currentDailyHours - newDailyHours) / currentDailyHours * 100).toFixed(0);

    return {
      tasksReorganized: totalTasks,
      tasksPrioritized: prioritizedCount,
      tasksPostponed: postponedCount,
      overdueReduction: Math.max(0, analysis.overdue - Math.floor(analysis.overdue * 0.3)),
      workloadReduction: `${workloadReduction}%`,
      estimatedCompletionRate: '75%',
      stressReduction: this.estimateStressReduction(analysis.crisisLevel),
      timeToCalm: '2-3 days',
      nextCheckIn: '1 week'
    };
  }

  /**
   * Calculate daily workload in hours
   * @private
   */
  calculateDailyWorkload(tasks) {
    const totalHours = tasks.reduce((sum, t) => sum + (t.estimated_hours || 2), 0);
    const totalDays = 30; // Assume 30-day window
    return totalHours / totalDays;
  }

  /**
   * Estimate stress reduction from rescue plan
   * @private
   */
  estimateStressReduction(crisisLevel) {
    const reductions = {
      'emergency': 'Significant (60-70%)',
      'crisis': 'Major (50-60%)',
      'overwhelmed': 'Moderate (40-50%)',
      'busy': 'Minor (20-30%)',
      'manageable': 'Optimization (10-20%)'
    };

    return reductions[crisisLevel] || 'Moderate (30-40%)';
  }

  /**
   * Store recovery plan in history
   * @private
   */
  async storeRecoveryPlan(userId, analysis, rescueStrategy, outcomes) {
    try {
      // Check if table exists first
      const { error: tableError } = await this.supabase
        .from('crisis_recovery_history')
        .select('id')
        .limit(1);

      // If table doesn't exist or has wrong schema, skip storage
      if (tableError && (tableError.code === '42P01' || tableError.code === '22001')) {
        console.log('[CrisisRescueService] Skipping recovery plan storage (table not configured)');
        return;
      }

      const { error } = await this.supabase
        .from('crisis_recovery_history')
        .insert({
          user_id: userId,
          crisis_level: analysis.crisisLevel || 'unknown',
          overdue_count: analysis.overdue || 0,
          urgent_count: analysis.urgent || 0,
          total_tasks: analysis.totalActive || 0,
          // Truncate long strings to fit VARCHAR(50)
          seasonal_phase: (analysis.seasonalPhase?.phase_name || 'Unknown').substring(0, 50),
          rescue_strategy: (rescueStrategy.strategy || 'unknown').substring(0, 50),
          recovery_plan: {
            strategy: rescueStrategy.strategy,
            prioritized_count: rescueStrategy.prioritizedTasks?.length || 0,
            postponed_count: rescueStrategy.postponed?.length || 0,
            actions: rescueStrategy.actions?.slice(0, 5) || [] // Limit to first 5 actions
          },
          execution_status: 'pending'
        });

      if (error) {
        console.warn('[CrisisRescueService] Failed to store recovery plan (non-critical):', error.message);
        // Don't throw - storage is optional
      }
    } catch (error) {
      console.warn('[CrisisRescueService] Error storing recovery plan (non-critical):', error.message);
      // Don't throw - storage is optional
    }
  }

  // === Action Generation Methods ===

  /**
   * Generate triage actions for task reorganization
   * @private
   */
  generateTriageActions(prioritized, postponed, currentDate) {
    const actions = [];

    // Reschedule postponed tasks
    postponed.forEach(task => {
      actions.push({
        type: 'reschedule',
        taskId: task.id,
        currentTitle: task.title,
        currentDate: task.due_date || task.scheduled_date,
        proposedDate: format(addDays(currentDate, 30), 'yyyy-MM-dd'),
        reasoning: 'Postponed to focus on Early Decision/Action applications'
      });
    });

    // Boost critical tasks
    prioritized.slice(0, 5).forEach(task => {
      if (!task.priority || task.priority !== 'critical') {
        actions.push({
          type: 'prioritize',
          taskId: task.id,
          currentTitle: task.title,
          currentPriority: task.priority,
          newPriority: 'critical',
          reasoning: 'Elevated to critical for ED/EA deadline'
        });
      }
    });

    return actions;
  }

  /**
   * Generate emergency actions for crisis mode
   * @private
   */
  generateEmergencyActions(critical, postponed, currentDate) {
    const actions = [];

    // Mark all critical tasks as must-complete
    critical.forEach(task => {
      actions.push({
        type: 'mark_critical',
        taskId: task.id,
        currentTitle: task.title,
        deadline: task.due_date || task.scheduled_date,
        reasoning: 'CRITICAL: Must complete for ED/EA deadline'
      });
    });

    // Defer all non-critical tasks
    postponed.forEach(task => {
      actions.push({
        type: 'defer',
        taskId: task.id,
        currentTitle: task.title,
        deferUntil: format(addDays(currentDate, 21), 'yyyy-MM-dd'),
        reasoning: 'Deferred during emergency triage'
      });
    });

    return actions;
  }

  /**
   * Generate pivot actions for November strategy
   * @private
   */
  generatePivotActions(salvageable, pivotToRD, rdTasks, currentDate) {
    const actions = [];

    // Mark salvageable as urgent
    salvageable.forEach(task => {
      actions.push({
        type: 'urgent',
        taskId: task.id,
        currentTitle: task.title,
        deadline: task.due_date,
        reasoning: 'URGENT: Last chance for ED/EA submission'
      });
    });

    // Convert pivot tasks to RD
    pivotToRD.forEach(task => {
      const newDate = format(addDays(currentDate, 45), 'yyyy-MM-dd');
      actions.push({
        type: 'convert_to_rd',
        taskId: task.id,
        currentTitle: task.title,
        currentRound: task.application_round,
        newRound: 'regular',
        newDate,
        reasoning: 'Pivoted to Regular Decision round'
      });
    });

    return actions;
  }

  /**
   * Generate restart actions for comprehensive reset
   * @private
   */
  generateRestartActions(redistributed, currentDate) {
    const actions = [];
    const weeklyBuckets = {};

    // Group tasks by week
    redistributed.forEach(task => {
      const weekNumber = Math.floor(differenceInDays(
        parseISO(task.newDueDate),
        currentDate
      ) / 7);

      if (!weeklyBuckets[weekNumber]) {
        weeklyBuckets[weekNumber] = [];
      }
      weeklyBuckets[weekNumber].push(task);
    });

    // Create weekly sprint actions
    Object.entries(weeklyBuckets).forEach(([week, tasks]) => {
      actions.push({
        type: 'weekly_sprint',
        week: parseInt(week) + 1,
        startDate: format(addDays(currentDate, week * 7), 'yyyy-MM-dd'),
        endDate: format(addDays(currentDate, (week + 1) * 7 - 1), 'yyyy-MM-dd'),
        taskCount: tasks.length,
        totalHours: tasks.reduce((sum, t) => sum + (t.estimated_hours || 2), 0),
        focus: this.determineWeeklyFocus(tasks)
      });
    });

    return actions;
  }

  /**
   * Generate deadline-focused actions
   * @private
   */
  generateDeadlineActions(immediate, thisWeek, nextWeek, currentDate) {
    const actions = [];

    // Create daily plan for immediate tasks
    if (immediate.length > 0) {
      actions.push({
        type: 'daily_sprint',
        date: format(currentDate, 'yyyy-MM-dd'),
        tasks: immediate.map(t => ({
          id: t.id,
          title: t.title,
          hours: t.estimated_hours || 2
        })),
        totalHours: immediate.reduce((sum, t) => sum + (t.estimated_hours || 2), 0),
        reasoning: 'Immediate deadline focus'
      });
    }

    // Create week plan
    if (thisWeek.length > 0) {
      actions.push({
        type: 'weekly_plan',
        weekStart: format(currentDate, 'yyyy-MM-dd'),
        weekEnd: format(addDays(currentDate, 7), 'yyyy-MM-dd'),
        taskCount: thisWeek.length,
        dailyHours: thisWeek.reduce((sum, t) => sum + (t.estimated_hours || 2), 0) / 7,
        reasoning: 'This week\'s deadlines'
      });
    }

    return actions;
  }

  // === Helper Methods ===

  /**
   * Create task batches for structured recovery
   * @private
   */
  createTaskBatches(batchConfig) {
    return batchConfig.map(config => ({
      ...config,
      tasks: config.tasks.slice(0, 10) // Limit batch size for manageability
    }));
  }

  /**
   * Calculate optimal due date for task redistribution
   * @private
   */
  calculateOptimalDueDate(task, currentDate) {
    // Base on cognitive intensity and priority
    let daysOut = 7; // Default one week

    if (task.cognitive_intensity === 'high') {
      daysOut = 14; // Two weeks for complex tasks
    } else if (task.cognitive_intensity === 'low') {
      daysOut = 3; // Quick tasks sooner
    }

    // Adjust for priority
    if (task.priority === 'critical') {
      daysOut = Math.min(daysOut, 3);
    } else if (task.priority === 'low') {
      daysOut = Math.max(daysOut, 21);
    }

    return format(addDays(currentDate, daysOut), 'yyyy-MM-dd');
  }

  /**
   * Redistribute tasks evenly across time period
   * @private
   */
  redistributeTasks(tasks, currentDate, daySpan) {
    const tasksPerDay = Math.ceil(tasks.length / daySpan);
    let currentDay = 0;

    return tasks.map((task, index) => {
      if (index % tasksPerDay === 0 && index > 0) {
        currentDay++;
      }

      return {
        ...task,
        newDueDate: format(addDays(currentDate, currentDay), 'yyyy-MM-dd')
      };
    });
  }

  /**
   * Get college priority for RD optimization
   * @private
   */
  getCollegePriority(collegeName, tasks) {
    // Prioritize based on task count and types
    let priority = 1.0;

    if (tasks.length > 5) priority *= 1.2; // More tasks = higher priority
    if (tasks.some(t => t.priority === 'critical')) priority *= 1.5;
    if (collegeName.toLowerCase().includes('safety')) priority *= 0.8; // Safety schools lower
    if (collegeName.toLowerCase().includes('reach')) priority *= 1.3; // Reach schools higher

    return priority;
  }

  /**
   * Determine weekly focus from task types
   * @private
   */
  determineWeeklyFocus(tasks) {
    const categories = {};

    tasks.forEach(task => {
      const cat = task.category || 'general';
      categories[cat] = (categories[cat] || 0) + 1;
    });

    // Return top category
    return Object.entries(categories)
      .sort((a, b) => b[1] - a[1])[0]?.[0] || 'mixed';
  }

  // === NEW: Consequence-Based Triage Methods ===

  /**
   * Identify catastrophic tasks - must complete regardless of circumstances
   * These are tasks where incompletion has severe negative consequences
   * (Contextually intelligent - surfaces what truly matters)
   * @param {Array} tasks - Timeline tasks
   * @returns {Array} Catastrophic tasks sorted by deadline urgency
   */
  identifyCatastrophicTasks(tasks) {
    return tasks
      .filter(task =>
        task.incompletion_cost >= 0.9 && // Catastrophic if not completed
        task.completion_status !== 'completed'
      )
      .sort((a, b) => {
        // Sort by deadline urgency
        const dateA = parseISO(a.due_date || a.scheduled_date);
        const dateB = parseISO(b.due_date || b.scheduled_date);
        return dateA - dateB;
      });
  }

  /**
   * Identify quality-priority tasks - where doing it well really matters
   * High consequence + high quality gap = invest the time to do it right
   * (Confidence-instilling - students know where quality matters)
   * @param {Array} tasks - Timeline tasks
   * @returns {Array} Quality priority tasks sorted by combined score
   */
  identifyQualityPriorityTasks(tasks) {
    return tasks
      .filter(task =>
        task.mvv_risk >= 0.7 && // High quality gap - MVV won't cut it
        task.incompletion_cost >= 0.7 && // Important to complete well
        task.completion_status !== 'completed'
      )
      .sort((a, b) => {
        // Weighted score: quality gap (60%) + consequence (40%)
        const scoreA = (a.mvv_risk * 0.6) + (a.incompletion_cost * 0.4);
        const scoreB = (b.mvv_risk * 0.6) + (b.incompletion_cost * 0.4);
        return scoreB - scoreA;
      });
  }

  /**
   * Suggest MVV candidates - tasks safe to rush when time-pressed
   * Low quality gap + has MVV approach = time-saving opportunity
   * (Strategically transparent - show time savings potential)
   * @param {Array} tasks - Timeline tasks
   * @returns {Array} MVV candidates with time savings
   */
  suggestMVVCandidates(tasks) {
    return tasks
      .filter(task =>
        task.mvv_risk <= 0.4 && // Low quality gap - MVV is fine
        task.mvv_time_required &&
        task.optimal_time_required &&
        task.optimal_time_required > task.mvv_time_required &&
        task.completion_status !== 'completed'
      )
      .map(task => ({
        ...task,
        timeSavings: task.optimal_time_required - task.mvv_time_required,
        recommendation: task.min_viable_version
      }))
      .sort((a, b) => b.timeSavings - a.timeSavings);
  }

  /**
   * Calculate total time savings from MVV candidates
   * @param {Array} mvvCandidates - Tasks from suggestMVVCandidates()
   * @returns {Object} Time savings summary
   */
  calculateTimeSavings(mvvCandidates) {
    const totalSavings = mvvCandidates.reduce(
      (sum, task) => sum + (task.timeSavings || 0),
      0
    );

    return {
      totalMinutes: totalSavings,
      totalHours: (totalSavings / 60).toFixed(1),
      taskCount: mvvCandidates.length,
      averageSavings: mvvCandidates.length > 0
        ? Math.round(totalSavings / mvvCandidates.length)
        : 0
    };
  }

  /**
   * Enhanced recovery plan generation with consequence-based separation
   * Divides tasks into "Must do well" vs "MVV acceptable" buckets
   * (Maximally assistive - clear guidance on where to invest quality time)
   * @param {Array} timeline - All timeline tasks
   * @param {Date} currentDate - Current date
   * @returns {Object} Enhanced recovery plan
   */
  generateConsequenceBasedRecoveryPlan(timeline, currentDate = new Date()) {
    // Identify different triage categories
    const catastrophic = this.identifyCatastrophicTasks(timeline);
    const qualityPriority = this.identifyQualityPriorityTasks(timeline);
    const mvvCandidates = this.suggestMVVCandidates(timeline);
    const timeSavings = this.calculateTimeSavings(mvvCandidates);

    // Calculate total hours required
    const qualityHours = qualityPriority.reduce((sum, t) =>
      sum + (t.optimal_time_required || t.estimated_hours * 60 || 120),
      0
    ) / 60;

    const mvvHours = mvvCandidates.reduce((sum, t) =>
      sum + (t.mvv_time_required || 30),
      0
    ) / 60;

    const remainingTasks = timeline.filter(t =>
      !catastrophic.find(c => c.id === t.id) &&
      !qualityPriority.find(q => q.id === t.id) &&
      !mvvCandidates.find(m => m.id === t.id) &&
      t.completion_status !== 'completed'
    );

    return {
      mustComplete: {
        label: 'Must Complete',
        description: 'Critical consequences if not done',
        tasks: catastrophic,
        count: catastrophic.length,
        strategy: 'Complete all, prioritize by deadline'
      },
      qualityMatters: {
        label: 'Quality Matters Here',
        description: 'Take time to do these well',
        tasks: qualityPriority,
        count: qualityPriority.length,
        estimatedHours: qualityHours,
        strategy: 'Use optimal approach, don\'t rush'
      },
      canRush: {
        label: 'Quick Wins Available',
        description: 'MVV approach is sufficient',
        tasks: mvvCandidates,
        count: mvvCandidates.length,
        estimatedHours: mvvHours,
        timeSavings: timeSavings,
        strategy: `Save ${timeSavings.totalHours} hours with MVV approach`
      },
      remaining: {
        label: 'Other Tasks',
        tasks: remainingTasks,
        count: remainingTasks.length
      },
      summary: {
        totalTasks: timeline.length,
        categorized: catastrophic.length + qualityPriority.length + mvvCandidates.length,
        potentialTimeSavings: `${timeSavings.totalHours} hours`
      }
    };
  }
}

// Export class only - instances should be created with supabase client parameter